import { OverlayPanel } from '@theia-lved-core/browser/panels/shared/overlay-panel';
import { CommandContribution, CommandRegistry, MenuContribution, MenuModelRegistry, nls } from '@theia/core';
import { animationFrame, CommonCommands, CommonMenus } from '@theia/core/lib/browser';
import { WidgetManager } from '@theia/core/lib/browser/widget-manager';
import { inject, injectable } from '@theia/core/shared/inversify';
import { PreferencesWidget } from '@theia/preferences/lib/browser/views/preference-widget';

@injectable()
export class SettingsContribution implements CommandContribution, MenuContribution {
    @inject(WidgetManager) protected readonly widgetManager!: WidgetManager;

    private overlay: OverlayPanel | undefined;

    async openSettings(args?: { query?: string } | string): Promise<PreferencesWidget> {
        const widget = await this.widgetManager.getOrCreateWidget<PreferencesWidget>(PreferencesWidget.ID);

        if (!this.overlay) {
            this.overlay = new OverlayPanel({ title: 'Settings' });
            this.overlay.setContent(widget);
        }

        const query = typeof args === 'string' ? args : args?.query;
        if (query) {
            await widget.setSearchTerm(query);
        }

        this.overlay.open();
        return widget;
    }

    async navigateToPreference(preferenceId: string): Promise<boolean> {
        const widget = await this.openSettings();
        const selector = `li[data-pref-id="${preferenceId}"]:not([data-node-id^="commonly-used@"])`;
        const element = widget.node.querySelector(selector);

        if (!(element instanceof HTMLElement)) {
            return false;
        }

        if (element.classList.contains('hidden')) {
            await widget.setSearchTerm('');
            await animationFrame();
        }

        element.scrollIntoView({ block: 'center' });
        element.focus();
        return true;
    }

    registerCommands(commands: CommandRegistry): void {
        commands.registerCommand(CommonCommands.OPEN_PREFERENCES, {
            execute: async (args?: { query?: string } | string) => {
                await this.openSettings(args);
            }
        });
    }

    registerMenus(menus: MenuModelRegistry): void {
        menus.registerMenuAction(CommonMenus.FILE_SETTINGS_SUBMENU_OPEN, {
            commandId: CommonCommands.OPEN_PREFERENCES.id,
            label: nls.localizeByDefault('Settings'),
            order: 'a10'
        });
        menus.registerMenuAction(CommonMenus.MANAGE_SETTINGS, {
            commandId: CommonCommands.OPEN_PREFERENCES.id,
            label: nls.localizeByDefault('Settings'),
            order: 'a10'
        });
    }
}
